import React, { useCallback, useEffect, useMemo } from 'react';

import { useItemMasterQuery } from 'shared/hooks/warehouse/useItemMasterQuery';

import { ExternalSkuPicker } from './ExternalSkuPicker';
import { ItemMasterPicker } from './ItemMasterPicker';

/**
 * Composes External SKU + Item Master pickers and applies PO item lookup behavior:
 * - External SKU is not required first
 * - Selecting item master sets external sku
 * - Changing external sku clears item master, and auto-selects when exactly one match exists
 *
 * This coordinator is intended to be reused across Create and Edit flows.
 */
export function PurchaseOrderItemLookup({
    enabled = true,
    externalSku = '',
    itemMasterSku = '',
    onExternalSkuChange,
    onItemMasterSkuChange,
}) {
    const { data: itemMasters = [] } = useItemMasterQuery({ activeOnly: true, enabled, mmOnly: true });

    const itemMastersBySku = useMemo(() => {
        const map = new Map();
        for (const row of itemMasters) {
            map.set(String(row.sku), row);
        }
        return map;
    }, [itemMasters]);

    const itemMastersByExternalSku = useMemo(() => {
        const map = new Map();
        for (const row of itemMasters) {
            const key = String(row.externalSku ?? '');
            if (key) {
                const existing = map.get(key);
                if (existing) existing.push(row);
                else map.set(key, [row]);
            }
        }
        return map;
    }, [itemMasters]);

    const handleExternalSkuChange = useCallback(
        (nextExternalSku) => {
            const next = String(nextExternalSku ?? '');
            const prev = String(externalSku ?? '');
            if (next === prev) return;

            onExternalSkuChange(next);

            // External SKU change clears item master, unless there is exactly one match.
            onItemMasterSkuChange('');
            const matches = itemMastersByExternalSku.get(next) ?? [];
            if (matches.length === 1) {
                onItemMasterSkuChange(String(matches[0].sku));
            }
        },
        [externalSku, itemMastersByExternalSku, onExternalSkuChange, onItemMasterSkuChange],
    );

    const handleItemMasterChange = useCallback(
        (nextItemMasterSku) => {
            const nextSku = String(nextItemMasterSku ?? '');
            onItemMasterSkuChange(nextSku);

            const match = itemMastersBySku.get(nextSku);
            if (match?.externalSku != null) {
                onExternalSkuChange(String(match.externalSku));
            }
        },
        [itemMastersBySku, onExternalSkuChange, onItemMasterSkuChange],
    );

    useEffect(() => {
        // If item masters load after an external sku selection, auto-select the single match.
        const externalSkuValue = String(externalSku ?? '');
        if (!externalSkuValue) return;
        if (String(itemMasterSku ?? '')) return;
        const matches = itemMastersByExternalSku.get(externalSkuValue) ?? [];
        if (matches.length === 1) {
            onItemMasterSkuChange(String(matches[0].sku));
        }
    }, [externalSku, itemMasterSku, itemMastersByExternalSku, onItemMasterSkuChange]);

    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ExternalSkuPicker
                value={String(externalSku ?? '')}
                onValueChange={handleExternalSkuChange}
                enabled={enabled}
            />
            <ItemMasterPicker
                value={String(itemMasterSku ?? '')}
                onValueChange={handleItemMasterChange}
                externalSkuFilter={String(externalSku ?? '') || undefined}
                enabled={enabled}
            />
        </div>
    );
}

PurchaseOrderItemLookup.displayName = 'PurchaseOrderItemLookup';
